/**
 * InventoryView.js
 *
 * Shared cargo + death-cache UI used by:
 *   - /app/frontend/src/pages/Inventory.js          (full /inventory route)
 *   - /app/frontend/src/components/InventoryOverlay.js (in-game I-key overlay)
 *
 * Provides Tabs (Cargo / Death Caches) with capacity bar, per-ore drop, total
 * credit estimate, and death-cache recovery actions. Stateless w.r.t. routing
 * — accepts inventory + deaths + handlers from the parent.
 */

import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Trash2, MapPin } from 'lucide-react';

export function InventoryView({
  loading = false,
  inventory = null,
  deaths = [],
  onDropOre,
  onClearAll,
  onRecoverCache,
}) {
  const ores = inventory?.ores || [];
  const shipCargo = inventory?.ship_cargo || [];
  const totalUsed = ores.reduce((a, o) => a + (o.qty || 0), 0);
  const totalValue = ores.reduce(
    (a, o) => a + (o.qty || 0) * (o.est_value_per_unit || 0),
    0,
  );
  const shipUsed = shipCargo.reduce((a, o) => a + (o.qty || 0), 0);
  const shipValue = shipCargo.reduce(
    (a, o) => a + (o.qty || 0) * (o.est_value_per_unit || 0),
    0,
  );
  const cap = inventory?.cargo_capacity || 0;
  const pct = cap > 0 ? totalUsed / cap : 0;
  const shipPct = cap > 0 ? shipUsed / cap : 0;

  if (loading) {
    return (
      <div className="text-muted-foreground text-sm" data-testid="inventory-loading">
        Loading…
      </div>
    );
  }

  return (
    <Tabs defaultValue="cargo" className="w-full">
      <TabsList>
        <TabsTrigger value="cargo" data-testid="inventory-tab-cargo">Cargo</TabsTrigger>
        <TabsTrigger value="deaths" data-testid="inventory-tab-deaths">
          Death Caches
          {deaths.length > 0 && <Badge variant="secondary" className="ml-2">{deaths.length}</Badge>}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="cargo" className="mt-4 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Personal Backpack</span>
              <span className="text-sm font-mono text-cyan-300" data-testid="inventory-capacity-label">
                {totalUsed.toFixed(1)} / {cap.toFixed(0)} u
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative w-full h-2 bg-cyan-900/40 rounded overflow-hidden mb-4">
              <div
                className="h-full bg-cyan-400 transition-[width] duration-200"
                style={{ width: `${Math.min(1, pct) * 100}%` }}
                data-testid="inventory-capacity-bar"
              />
            </div>

            {ores.length > 0 ? (
              <div className="space-y-2">
                {ores.map((o) => (
                  <div
                    key={o.symbol}
                    className="flex items-center justify-between px-3 py-2 rounded-md border border-cyan-400/20 bg-[#02060f]/40"
                    data-testid={`inventory-ore-row-${o.symbol}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-md border border-cyan-400/30 flex items-center justify-center bg-[#0a1726]"
                        style={{ fontFamily: 'Source Code Pro, monospace' }}
                      >
                        <span className="text-cyan-200 font-semibold">{o.symbol}</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium">{o.name}</div>
                        <div
                          className="text-xs text-muted-foreground"
                          style={{ fontFamily: 'Source Code Pro, monospace' }}
                        >
                          {(o.qty || 0).toFixed(2)} u · {Number(o.est_value_per_unit || 0).toFixed(2)} cr/u
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div
                        className="text-amber-300 text-sm tabular-nums"
                        style={{ fontFamily: 'Source Code Pro, monospace' }}
                      >
                        ≈ {Math.round((o.qty || 0) * (o.est_value_per_unit || 0)).toLocaleString()} cr
                      </div>
                      {onDropOre && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDropOre(o.symbol)}
                          data-testid={`inventory-drop-${o.symbol}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}

                <div className="mt-4 flex items-center justify-between border-t border-cyan-400/20 pt-3">
                  <div className="text-sm text-muted-foreground">Estimated value</div>
                  <div
                    className="text-lg text-amber-300 tabular-nums"
                    data-testid="inventory-total-value"
                    style={{ fontFamily: 'Source Code Pro, monospace' }}
                  >
                    ≈ {Math.round(totalValue).toLocaleString()} cr
                  </div>
                </div>

                {onClearAll && (
                  <div className="mt-3 flex justify-end">
                    <Button variant="outline" size="sm" onClick={onClearAll} data-testid="inventory-clear-all">
                      <Trash2 className="w-4 h-4 mr-2" /> Discard All
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground italic" data-testid="inventory-empty">
                Backpack is empty. Mine deposits to fill it.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="text-amber-200">Ship Cargo Hold</span>
              <span className="text-sm font-mono text-amber-300" data-testid="inventory-ship-cap-label">
                {shipUsed.toFixed(1)} / {cap.toFixed(0)} u
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative w-full h-2 bg-amber-900/30 rounded overflow-hidden mb-4">
              <div
                className="h-full bg-amber-400 transition-[width] duration-200"
                style={{ width: `${Math.min(1, shipPct) * 100}%` }}
                data-testid="inventory-ship-cap-bar"
              />
            </div>
            {shipCargo.length > 0 ? (
              <div className="space-y-2">
                {shipCargo.map((o) => (
                  <div
                    key={o.symbol}
                    className="flex items-center justify-between px-3 py-2 rounded-md border border-amber-400/20 bg-[#1a1206]/40"
                    data-testid={`inventory-ship-row-${o.symbol}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-md border border-amber-400/30 flex items-center justify-center bg-[#1a1206]"
                        style={{ fontFamily: 'Source Code Pro, monospace' }}
                      >
                        <span className="text-amber-200 font-semibold">{o.symbol}</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium">{o.name}</div>
                        <div
                          className="text-xs text-muted-foreground"
                          style={{ fontFamily: 'Source Code Pro, monospace' }}
                        >
                          {(o.qty || 0).toFixed(2)} u
                          {o.est_value_per_unit ? ` · ${Number(o.est_value_per_unit).toFixed(2)} cr/u` : ''}
                        </div>
                      </div>
                    </div>
                    <div
                      className="text-amber-300 text-sm tabular-nums"
                      style={{ fontFamily: 'Source Code Pro, monospace' }}
                    >
                      ≈ {Math.round((o.qty || 0) * (o.est_value_per_unit || 0)).toLocaleString()} cr
                    </div>
                  </div>
                ))}
                <div className="mt-4 flex items-center justify-between border-t border-amber-400/20 pt-3">
                  <div className="text-sm text-muted-foreground">Estimated value</div>
                  <div
                    className="text-lg text-amber-300 tabular-nums"
                    data-testid="inventory-ship-total-value"
                    style={{ fontFamily: 'Source Code Pro, monospace' }}
                  >
                    ≈ {Math.round(shipValue).toLocaleString()} cr
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground italic" data-testid="inventory-ship-empty">
                Ship cargo hold is empty. Press G near your parked ship to stow your backpack.
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="deaths" className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Active Death Caches</CardTitle>
          </CardHeader>
          <CardContent>
            {deaths.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground italic" data-testid="deaths-empty">
                No death caches. Lucky you.
              </div>
            ) : (
              <div className="space-y-3">
                {deaths.map((d) => (
                  <div
                    key={d.id}
                    className="p-3 rounded-md border border-amber-400/30 bg-[#1a1206]/50"
                    data-testid={`death-cache-row-${d.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-amber-300" />
                          System <span className="font-mono text-amber-200">{d.coords_key}</span>
                          {' · '}Planet {d.planet_idx}
                          {d.moon_idx !== null && d.moon_idx !== undefined ? ` · Moon ${d.moon_idx}` : ''}
                        </div>
                        <div
                          className="text-xs text-muted-foreground mt-1"
                          style={{ fontFamily: 'Source Code Pro, monospace' }}
                        >
                          Pos: ({Math.round(d.pos.x)}, {Math.round(d.pos.y)}, {Math.round(d.pos.z)})
                          {d.credits_dropped > 0 && (
                            <span className="ml-2 text-amber-300">· {d.credits_dropped} cr</span>
                          )}
                        </div>
                        {d.ores?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {d.ores.map((o, i) => (
                              <Badge key={i} variant="outline" className="text-[10px]">
                                {o.symbol} × {Number(o.qty || 0).toFixed(1)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      {onRecoverCache && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onRecoverCache(d)}
                          data-testid={`death-cache-recover-${d.id}`}
                        >
                          Recover
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}

export default InventoryView;
